import React, { createContext, useCallback, useContext, useState } from 'react';
import { createGuestSession } from '../services/guestApi';

const GUEST_ID_KEY = 'guestId';

interface GuestContextValue {
  guestId: string | null;
  isReady: boolean;
  error: string | null;
  /** Ensure we have a guest session; call on /guest entry. Returns guestId or throws. */
  ensureSession: () => Promise<string>;
  clearGuest: () => void;
}

const GuestContext = createContext<GuestContextValue | null>(null);

export function GuestProvider({ children }: { children: React.ReactNode }) {
  const [guestId, setGuestIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(GUEST_ID_KEY);
  });
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ensureSession = useCallback(async (): Promise<string> => {
    const stored = localStorage.getItem(GUEST_ID_KEY);
    if (stored) {
      setGuestIdState(stored);
      setIsReady(true);
      setError(null);
      return stored;
    }
    try {
      const res = await createGuestSession();
      const id = res.guestId;
      if (id) {
        localStorage.setItem(GUEST_ID_KEY, id);
        setGuestIdState(id);
        setIsReady(true);
        setError(null);
        return id;
      }
      throw new Error('No guestId in response');
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to start guest session';
      setError(message);
      setIsReady(true);
      throw e;
    }
  }, []);

  const clearGuest = useCallback(() => {
    localStorage.removeItem(GUEST_ID_KEY);
    setGuestIdState(null);
    setIsReady(false);
    setError(null);
  }, []);

  const value: GuestContextValue = {
    guestId,
    isReady,
    error,
    ensureSession,
    clearGuest,
  };

  return <GuestContext.Provider value={value}>{children}</GuestContext.Provider>;
}

export function useGuest(): GuestContextValue {
  const ctx = useContext(GuestContext);
  if (!ctx) {
    throw new Error('useGuest must be used within GuestProvider');
  }
  return ctx;
}

/** Alias for useGuest (used by guest search/topic pages). */
export const useGuestContext = useGuest;

export function useGuestOptional(): GuestContextValue | null {
  return useContext(GuestContext);
}
